// Building blocks shared by the Broadcasts tabs. They wrap the system's own
// components (ReusableDropdown, DatePicker) and the Departments-style modal
// frame so every tab looks like the rest of the app.
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import ReusableDropdown from "../../../components/ReusableDropdown";
import DatePicker from "../../../components/DatePicker";
import { useBroadcastPage } from "../context";

/**
 * Modal frame: fixed header with close, scrollable body, footer for actions.
 * Portaled to <body>: the glass cards use backdrop-filter, which turns them
 * into the containing block for position:fixed — rendered inside one, the
 * overlay would be pinned to the card instead of the viewport.
 */
export const Modal = ({ title, icon: Icon, onClose, children, footer, maxWidth = 560, busy = false }) => {
  const { s, t } = useBroadcastPage();
  const close = () => {
    if (!busy) onClose?.();
  };
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  return createPortal(
    <div className="bc-scope" style={s.overlay} onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div role="dialog" aria-modal="true" aria-label={title} style={s.modal(maxWidth)}>
        <div style={s.modalHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            {Icon && (
              <span style={s.modalTitleIcon}>
                <Icon size={26} />
              </span>
            )}
            <h2 style={s.modalTitle}>{title}</h2>
          </div>
          <button type="button" className="bc-icon-btn" style={s.iconBtn()} onClick={close} disabled={busy} aria-label={t.close} title={t.close}>
            <X size={18} />
          </button>
        </div>
        <div className="bc-modal-body" style={s.modalBody}>
          {children}
        </div>
        {footer && <div style={s.modalFooter}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

/** Glass section card with an icon (or step number) title, subtitle and actions. */
export const SectionCard = ({ icon: Icon, step, title, subtitle, actions, children, style }) => {
  const { s, screenSize } = useBroadcastPage();
  return (
    <div style={{ ...s.sectionCard, ...style }}>
      <div style={s.sectionHeader}>
        <div style={{ minWidth: 0 }}>
          <h3 style={s.sectionTitle}>
            {step != null ? <span style={s.stepNumber}>{step}</span> : Icon ? <Icon size={screenSize.isMobile ? 16 : 20} /> : null}
            {title}
          </h3>
          {subtitle && <p style={s.sectionSubtitle}>{subtitle}</p>}
        </div>
        {actions && <div style={s.sectionActions}>{actions}</div>}
      </div>
      {children}
    </div>
  );
};

/** Blue group heading inside a modal (Add Employee modal style), optional action on the far side. */
export const GroupTitle = ({ icon: Icon, children, action, style }) => {
  const { s } = useBroadcastPage();
  return (
    <div style={{ ...s.groupTitle, ...style }}>
      <span style={s.groupTitleText}>
        {Icon && <Icon size={15} />}
        {children}
      </span>
      {action}
    </div>
  );
};

export const Field = ({ label, hint, children, style }) => {
  const { s } = useBroadcastPage();
  return (
    <div style={{ ...s.field, ...style }}>
      {label && <label style={s.label}>{label}</label>}
      {children}
      {hint && <p style={s.hint}>{hint}</p>}
    </div>
  );
};

/** ReusableDropdown in "plain select" mode with { value, label } options. */
export const Select = ({ value, onChange, options, placeholder, searchable, disabled, ariaLabel }) => {
  const { s, theme, language } = useBroadcastPage();
  return (
    <ReusableDropdown
      items={options}
      value={value ?? ""}
      onChange={onChange}
      placeholder={placeholder || ""}
      getItemKey={(o) => o.value}
      getItemLabel={(o) => o.label}
      searchable={searchable ?? options.length > 8}
      disabled={disabled}
      ariaLabel={ariaLabel}
      inputStyle={s.input}
      theme={theme}
      language={language}
    />
  );
};

const pad = (n) => String(n).padStart(2, "0");
export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Date (shared DatePicker) + time input. Value is local wall-clock time as
 * "YYYY-MM-DDTHH:mm" — the same shape <input type="datetime-local"> used.
 */
export const DateTimeField = ({ value, onChange, minDate, defaultTime = "09:00" }) => {
  const { s, theme, isRTL, t } = useBroadcastPage();
  const [date, time] = value ? String(value).split("T") : ["", ""];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)", gap: "8px" }}>
      <DatePicker
        theme={theme}
        isRTL={isRTL}
        value={date}
        minDate={minDate}
        placeholder={t.compose.date}
        onChange={(d) => onChange(d ? `${d}T${time || defaultTime}` : "")}
      />
      <input
        type="time"
        className="bc-input"
        aria-label={t.compose.time}
        style={{ ...s.input, direction: "ltr" }}
        value={time || ""}
        onChange={(e) => onChange(`${date || todayIso()}T${e.target.value || defaultTime}`)}
      />
    </div>
  );
};

export const Segmented = ({ value, onChange, options }) => {
  const { s } = useBroadcastPage();
  return (
    <div style={s.segmented} role="radiogroup">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} style={s.segment(value === o.value)} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
};

export const Toggle = ({ checked, onChange, label, hint, disabled }) => {
  const { s, theme } = useBroadcastPage();
  return (
    <label
      style={{
        display: "flex",
        gap: "12px",
        alignItems: "flex-start",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        marginBottom: "14px",
      }}
    >
      <input
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ position: "absolute", opacity: 0, width: 1, height: 1 }}
      />
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: "38px",
          height: "22px",
          borderRadius: "999px",
          marginTop: "1px",
          background: checked ? "#2e7cba" : theme === "dark" ? "rgba(255,255,255,0.18)" : "#cbd5e1",
          position: "relative",
          transition: "background 0.15s",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: "3px",
            insetInlineStart: checked ? "19px" : "3px",
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            background: "#fff",
            transition: "inset-inline-start 0.15s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}
        />
      </span>
      <span>
        <span style={{ fontSize: "14px", fontWeight: 600, color: s.colors.text }}>{label}</span>
        {hint && <span style={{ display: "block", fontSize: "12.5px", color: s.colors.muted, marginTop: "2px", lineHeight: 1.5 }}>{hint}</span>}
      </span>
    </label>
  );
};

/** System button: kind = primary | success | danger | secondary. */
export const Btn = ({ kind = "primary", small, disabled, children, style, ...rest }) => {
  const { s } = useBroadcastPage();
  const base = { primary: s.btnPrimary, success: s.btnSuccess, danger: s.btnDanger, secondary: s.btnSecondary }[kind] || s.btnPrimary;
  return (
    <button
      type="button"
      className={kind === "primary" ? "bc-btn-primary" : undefined}
      disabled={disabled}
      style={{ ...base(disabled), ...(small ? s.btnSmall : null), ...style }}
      {...rest}
    >
      {children}
    </button>
  );
};

export const IconBtn = ({ icon: Icon, label, onClick, tone, disabled }) => {
  const { s } = useBroadcastPage();
  return (
    <button
      type="button"
      className={`bc-icon-btn${tone === "danger" ? " danger" : ""}`}
      style={s.iconBtn(tone)}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      <Icon size={16} />
    </button>
  );
};

export const EmptyState = ({ icon: Icon, children }) => {
  const { s } = useBroadcastPage();
  return (
    <div style={s.emptyState}>
      {Icon && <Icon size={42} style={s.emptyIcon} />}
      <p style={s.emptyTitle}>{children}</p>
    </div>
  );
};
